// industryTheme.js — "Industry" dark wireframe theme for MUI (XAUPilot redesign)
// Steel-on-dark, square corners, Barlow type, flat surfaces — no gradients, no glass.
//
// Drop this in and feed it to your existing ThemeProvider (see INSTALL.md).
// Every MUI page (Dashboard, Subscription, Profile, Pricing, Footer, auth) re-skins
// from these tokens — no per-page changes needed for anything that reads the theme.
//
// Requires the Barlow + Barlow Condensed fonts to be loaded (see industry.css / INSTALL.md).

import { createTheme } from '@mui/material/styles';

// ---- Raw tokens (exported so components can reuse them) --------------------
export const industryTokens = {
  bg:        '#15171a', // page ground
  surface:   '#1e2126', // card / paper
  surface2:  '#22262c', // raised paper
  text:      '#eceef0',
  textDim:   'rgba(236,238,240,0.62)',
  textFaint: 'rgba(236,238,240,0.42)',
  divider:   'rgba(236,238,240,0.15)',
  accent:    '#5980a6', // the single steel accent
  accent400: '#7ba0c4', // hover on dark (one step lighter)
  accent700: '#3f6288', // pressed
  accentTint:'rgba(89,128,166,0.16)',
  pos:       '#46a07a', // muted green — the only "up" color
  posInk:    '#86c9a6',
  neg:       '#cf6f61', // muted red — the only "down" color
  negInk:    '#e59a8f',
  fontBody:    '"Barlow", system-ui, -apple-system, sans-serif',
  fontHeading: '"Barlow Condensed", "Barlow", system-ui, sans-serif',
  radius: 2, // square-cornered wireframe
};

const t = industryTokens;

const industryTheme = createTheme({
  palette: {
    mode: 'dark',
    primary:   { main: t.accent, light: t.accent400, dark: t.accent700, contrastText: t.text },
    secondary: { main: t.accent, contrastText: t.text },
    success:   { main: t.pos, light: t.posInk, contrastText: t.bg },
    error:     { main: t.neg, light: t.negInk, contrastText: t.bg },
    warning:   { main: '#c79a4b', contrastText: t.bg },
    info:      { main: t.accent, contrastText: t.text },
    background:{ default: t.bg, paper: t.surface },
    text:      { primary: t.text, secondary: t.textDim, disabled: t.textFaint },
    divider:   t.divider,
    grey: {
      500: 'rgba(236,238,240,0.45)',
      600: 'rgba(236,238,240,0.55)',
      900: t.surface,
    },
  },

  shape: { borderRadius: t.radius },

  typography: {
    fontFamily: t.fontBody,
    h1: { fontFamily: t.fontHeading, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.05 },
    h2: { fontFamily: t.fontHeading, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.08 },
    h3: { fontFamily: t.fontHeading, fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontFamily: t.fontHeading, fontWeight: 700 },
    h5: { fontFamily: t.fontHeading, fontWeight: 600 },
    h6: { fontFamily: t.fontHeading, fontWeight: 600, letterSpacing: '0.01em' },
    subtitle1: { fontWeight: 600 },
    button: { fontFamily: t.fontHeading, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' },
    overline: { fontFamily: t.fontHeading, fontWeight: 600, letterSpacing: '0.16em' },
  },

  components: {
    // Kill MUI's dark-mode "elevation gradient" overlay on every Paper/Card/Menu.
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: t.divider },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: t.surface,
          backgroundImage: 'none',
          border: `1px solid ${t.divider}`,
          borderRadius: t.radius,
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          backgroundColor: t.bg,
          backgroundImage: 'none',
          borderBottom: `1px solid ${t.divider}`,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: t.radius, paddingInline: 18, paddingBlock: 9 },
        containedPrimary: {
          color: t.text,
          '&:hover': { backgroundColor: t.accent400, color: t.bg },
          '&:active': { backgroundColor: t.accent700 },
        },
        outlined: {
          borderColor: t.divider,
          color: t.text,
          '&:hover': { borderColor: t.accent, backgroundColor: t.accentTint },
        },
        text: { '&:hover': { backgroundColor: t.accentTint } },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: t.radius, '&:hover': { backgroundColor: t.accentTint } },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: t.radius,
          borderColor: t.divider,
          color: t.textDim,
          fontFamily: t.fontHeading,
          letterSpacing: '0.04em',
          '&.Mui-selected': {
            backgroundColor: t.accent,
            color: t.text,
            '&:hover': { backgroundColor: t.accent400, color: t.bg },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: t.radius, fontWeight: 600, letterSpacing: '0.02em' },
        outlined: { borderColor: t.divider },
        colorSuccess: { color: t.posInk },
        colorError: { color: t.negInk },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: t.radius,
          backgroundColor: 'transparent',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.divider },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(236,238,240,0.3)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.accent, borderWidth: 1 },
        },
      },
    },

    MuiTextField: { defaultProps: { variant: 'outlined' } },

    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: t.accent, height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: t.fontHeading,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'none',
          color: t.textDim,
          '&.Mui-selected': { color: t.accent },
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: { '& .MuiTableCell-root': {
          color: t.textDim, fontFamily: t.fontHeading, fontWeight: 600,
          letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 12,
          borderBottom: `1px solid ${t.divider}`,
        } },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${t.divider}`, fontVariantNumeric: 'tabular-nums' },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: t.divider } } },

    MuiLink: {
      styleOverrides: { root: { color: t.accent, textDecorationColor: 'rgba(89,128,166,0.4)' } },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: t.surface2, border: `1px solid ${t.divider}`, borderRadius: t.radius, color: t.text },
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: t.bg, color: t.text },
        '::selection': { backgroundColor: t.accentTint },
        // Themed keyboard focus everywhere
        'a:focus-visible, button:focus-visible, [role="button"]:focus-visible': {
          outline: `2px solid ${t.accent}`,
          outlineOffset: '2px',
        },
      },
    },
  },
});

export default industryTheme;
